import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, signal, Component, OnDestroy, OnInit, inject, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { EMPTY, Observable, catchError, finalize, firstValueFrom, forkJoin, of, switchMap, tap } from 'rxjs';

import type { CentralTransferenciaDto, CorrespondenciaHtmlDto } from '@/core/api/sicom-api/registro/medico/central-transferencia/central-transferencia.model';
import { CentralTransferenciaService } from '@/core/api/sicom-api/registro/medico/central-transferencia/central-transferencia.service';
import { PermissaoService } from '@/core/api/sicom-api/portal/permissao/permissao.service';
import { AuthService } from '@/core/auth/auth.service';
import { BotaoAcoes, EInconPos } from '@/shared/components/layout/botao-acoes/botao-acoes';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import { CardContent } from '@/shared/components/card-content/card-content';
import { DateUtils } from '@/shared/utils/date-utils';
import { ConfirmationService, MenuItem, PrimeTemplate } from 'primeng/api';
import { ButtonDirective } from 'primeng/button';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { Dialog } from 'primeng/dialog';
import { InputText } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { CustomTableComponent, TableMetadata } from '@/shared/components/layout/custom-table/custom-table.component';
import { construirMetadataTransferencias } from '../central-transferencia-tabela.config';
import { FotosMedicosService } from '@/shared/services/fotos-medicos.service';
import { IdentificacaoPessoa } from '@/shared/components/pessoa/identificacao-pessoa/identificacao-pessoa';
import { severidadeDataValidade } from '../validade-solicitacao';
import { VigenciaSolicitacao } from '../vigencia-solicitacao/vigencia-solicitacao';
import { CartaoTransferencia } from '../cartao-transferencia/cartao-transferencia';
import { BandeiraUf } from '../bandeira-uf/bandeira-uf';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
interface FiltroAbertasUfDestino {
    id: string;
    nomeMedico: string;
    crm: string;
    cpf: string;
    ufOrigem: string;
    tipo: string;
}

type ChaveFiltroAbertasUfDestino = keyof FiltroAbertasUfDestino;

@Component({
    selector: 'app-abertas-uf-destino',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        DialogoPadraoDirective,
        CommonModule,
        FormsModule,
        ButtonDirective,
        InputText,
        Select,
        CardContent,
        BotaoAcoes,
        Dialog,
        ConfirmDialog,
        CustomTableComponent,
        IdentificacaoPessoa,
        VigenciaSolicitacao,
        BandeiraUf,
        CartaoTransferencia,
        PrimeTemplate
    ],
    providers: [ConfirmationService, FotosMedicosService],
    templateUrl: './abertas-uf-destino.html',
    styleUrl: './abertas-uf-destino.scss'
})
/** Central de transferência: solicitações abertas para a UF de destino. */
export class AbertasUfDestinoComponent implements OnInit, OnDestroy {
    customTable = viewChild(CustomTableComponent);

    protected readonly EInconPos = EInconPos;
    private readonly authService = inject(AuthService);
    private readonly centralTransferenciaService = inject(CentralTransferenciaService);
    private readonly fotosMedicos = inject(FotosMedicosService);
    private readonly permissaoService = inject(PermissaoService);
    private readonly confirmationService = inject(ConfirmationService);
    private readonly sanitizer = inject(DomSanitizer);

    first = 0;
    rows = 10;

    metadataTabela: TableMetadata = construirMetadataTransferencias('UF Origem', 'ufOrigemStr');

    get totalSolicitacoes(): number {
        return this.solicitacoesFiltradas.length;
    }

    get totalTransferencias(): number {
        return this.solicitacoesFiltradas.filter((s) => (s.tipoSolicitacao ?? '').trim().toUpperCase() === 'T' || (s.descricaoTipoSolicitacao ?? '').trim().toUpperCase().includes('TRANSFER')).length;
    }

    get totalSecundarias(): number {
        return this.solicitacoesFiltradas.filter((s) => (s.tipoSolicitacao ?? '').trim().toUpperCase() === 'S' || (s.descricaoTipoSolicitacao ?? '').trim().toUpperCase().includes('SECUND')).length;
    }

    get totalVencidos(): number {
        return this.solicitacoesFiltradas.filter((s) => severidadeDataValidade(s.dataValidade) === 'danger').length;
    }

    get solicitacoesFiltradasPaginadas(): CentralTransferenciaDto[] {
        return this.solicitacoesFiltradas.slice(this.first, this.first + this.rows);
    }

    get solicitacoesFiltradasTabela() {
        return this.solicitacoesFiltradasPaginadas.map((item) => ({
            ...item,
            idSolicitacaoStr: String(item.idSolicitacao ?? '-'),
            // Inscrição do médico: CRM com a UF DELE, no formato do card do quadro. UF ausente
            // faz a linha sumir — melhor que "CRM 4343/" pela metade.
            crmUfChip: item.crm && item.ufOrigem ? `CRM ${item.crm}/${item.ufOrigem.toUpperCase()}` : '',
            medicoPerfil: this.fotosMedicos.perfilDoMedico(item.nomeMedico, item.crm, item.ufOrigem),
            ufOrigemStr: this.formatarUf(item.ufOrigem),
            ufDestinoStr: this.formatarUf(item.ufDestino),
            tipoSolicitacaoTag: {
                value: this.obterTipoSolicitacao(item),
                severity: this.obterSeveridadeTipo(item)
            },
            statusStr: this.obterStatus(item),
            dataSolicitacaoStr: this.formatarData(item.dataSolicitacao),
            dataValidadeTag: {
                value: this.formatarData(item.dataValidade),
                severity: severidadeDataValidade(item.dataValidade)
            }
        }));
    }

    onPageChange(event: { first?: number; rows?: number }): void {
        this.first = event.first ?? 0;
        this.rows = event.rows ?? this.rows;
    }

    ufDestino = '';
    nomeUsuario = '';
    carregando = signal(false);
    carregandoAcao = signal(false);
    erroConsulta = '';
    feedbackAcao = '';
    erroAcao = false;
    solicitacoes: CentralTransferenciaDto[] = [];
    solicitacoesFiltradas: CentralTransferenciaDto[] = [];
    solicitacaoSelecionada: CentralTransferenciaDto | null = null;
    filtrosVisiveis = false;
    filtrosAplicados: FiltroAbertasUfDestino = this.criarFiltrosVazios();
    filtrosEdicao: FiltroAbertasUfDestino = this.criarFiltrosVazios();
    blocosColapsados: Record<ChaveFiltroAbertasUfDestino, boolean> = {
        id: true,
        nomeMedico: true,
        crm: true,
        cpf: true,
        ufOrigem: true,
        tipo: true
    };
    readonly opcoesTipo = [
        { label: 'Todos', value: '' },
        { label: 'Transferência', value: 'T' },
        { label: 'Inscrição Secundária', value: 'S' }
    ];
    dialogCorrespondenciaVisivel = false;
    dialogCarregando = signal(false);
    dialogErro = '';
    dialogTitulo = '';
    correspondenciasHtml: Array<{ titulo: string; html: string }> = [];
    certificadoPdfUrl: SafeResourceUrl | null = null;
    private _rawPdfUrl: string | null = null;
    dialogLoadingConfirmacaoVisivel = false;
    dialogCrmGeradoVisivel = false;
    crmGeradoConfirmacao = '';
    ufCrmGeradoConfirmacao = '';
    podeExibirAcoes = signal(false);

    readonly itensAcoesMenu: MenuItem[] = [
        {
            label: 'Confirmar',
            icon: 'pi pi-check',
            styleClass: 'bg-green-500',
            permission: PERMISSOES.medicos.centralTransferencia.gerir,
            command: () => this.confirmar()
        },
        {
            label: 'Repudiar',
            icon: 'pi pi-times',
            styleClass: 'bg-red-500',
            permission: PERMISSOES.medicos.centralTransferencia.gerir,
            command: () => this.repudiar()
        },
        {
            label: 'Visualizar Correspondências',
            icon: 'pi pi-file',
            styleClass: 'bg-blue-500',
            command: () => this.visualizarCorrespondencias()
        }
    ];

    async ngOnInit(): Promise<void> {
        const user = await this.authService.getUserInfo();
        this.nomeUsuario = user?.name ?? '';
        this.carregarAbertasUfDestino();
    }

    ngOnDestroy(): void {
        this.revogarUrlPdf();
    }

    carregarAbertasUfDestino(): void {
        this.erroConsulta = '';
        this.solicitacoes = [];
        this.solicitacoesFiltradas = [];
        this.solicitacaoSelecionada = null;
        this.first = 0;
        this.customTable()?.selectedRow.set(null);
        this.podeExibirAcoes.set(false);

        const ufLogada = this.authService.ufDoUsuario()?.trim().toUpperCase() ?? '';
        this.ufDestino = ufLogada;

        if (!ufLogada) {
            this.erroConsulta = 'Não foi possível identificar a UF do usuário logado.';
            return;
        }

        void this.atualizarPermissaoAcoes(ufLogada);

        this.carregando.set(true);

        this.centralTransferenciaService
            .listarAbertosDestino(ufLogada)
            .pipe(
                finalize(() => {
                    this.carregando.set(false);
                })
            )
            .subscribe({
                next: (response) => {
                    this.solicitacoes = response ?? [];
                    // Retratos entram depois, um por CRM/UF: a foto é enfeite e não pode segurar a
                    // lista. Quando cada uma chega, o mapa do serviço muda e a tabela se redesenha.
                    this.fotosMedicos.carregar(this.solicitacoes.map((solicitacao) => ({ crm: solicitacao.crm, uf: solicitacao.ufOrigem })));
                    this.aplicarListaFiltrada();
                },
                error: (erro) => {
                    this.erroConsulta = this.extrairMensagemErro(erro) ?? 'Não foi possível carregar as solicitações abertas para a UF de destino.';
                }
            });
    }

    formatarData(valor?: string | null): string {
        const data = (valor ?? '').trim();
        if (!data) {
            return '-';
        }

        if (DateUtils.isDateString(data)) {
            return DateUtils.formatDate(data);
        }

        return data;
    }

    obterTipoSolicitacao(item: CentralTransferenciaDto): string {
        return this.valorTexto(item.descricaoTipoSolicitacao) || this.valorTexto(item.tipoSolicitacao) || '-';
    }

    obterStatus(item: CentralTransferenciaDto): string {
        return this.valorTexto(item.descricaoStatus) || this.valorTexto(item.status) || '-';
    }

    obterSeveridadeTipo(item: CentralTransferenciaDto): 'info' | 'success' | 'secondary' {
        const tipoCodigo = this.valorTexto(item.tipoSolicitacao).toUpperCase();
        const tipoDescricao = this.valorTexto(item.descricaoTipoSolicitacao).toUpperCase();

        if (tipoCodigo === 'S' || tipoDescricao.includes('SECUND')) {
            return 'success';
        }

        if (tipoCodigo === 'T' || tipoDescricao.includes('TRANSFER')) {
            return 'info';
        }

        return 'secondary';
    }

    formatarUf(uf?: string | null): string {
        const valor = (uf ?? '').trim().toUpperCase();
        return valor || '-';
    }

    async onSelecaoAlterada(valor: CentralTransferenciaDto | null): Promise<void> {
        this.solicitacaoSelecionada = valor;
        await this.atualizarPermissaoAcoes(this.ufDestino);
    }

    alternarFiltros(): void {
        this.filtrosVisiveis = !this.filtrosVisiveis;
    }

    alternarColapso(chave: ChaveFiltroAbertasUfDestino): void {
        this.blocosColapsados[chave] = !this.blocosColapsados[chave];
    }

    iconeColapso(chave: ChaveFiltroAbertasUfDestino): string {
        return this.blocosColapsados[chave] ? 'pi pi-chevron-down' : 'pi pi-chevron-up';
    }

    aplicarFiltros(): void {
        this.filtrosAplicados = { ...this.filtrosEdicao };
        this.first = 0;
        this.aplicarListaFiltrada();
    }

    restaurarFiltros(): void {
        this.filtrosEdicao = { ...this.filtrosAplicados };
    }

    limparFiltros(): void {
        this.filtrosAplicados = this.criarFiltrosVazios();
        this.filtrosEdicao = this.criarFiltrosVazios();
        this.first = 0;
        this.aplicarListaFiltrada();
    }

    confirmar(): void {
        this.confirmarAcaoUsuario('Confirmar', 'Deseja realmente confirmar esta solicitação?', () => {
            this.executarConfirmacaoComCadastro();
        });
    }

    repudiar(): void {
        this.confirmarAcaoUsuario('Repudiar', 'Deseja realmente repudiar esta solicitação?', () => {
            this.executarAcao('repudiar', 'Repúdio realizado com sucesso.', (idSolicitacao, crm, uf) => this.centralTransferenciaService.repudiar(idSolicitacao, crm, uf));
        });
    }

    visualizarCorrespondencias(): void {
        this.abrirDialogCorrespondencia('Visualizar Correspondências');
    }

    fecharDialogCorrespondencia(): void {
        this.revogarUrlPdf();
        this.certificadoPdfUrl = null;
        this.dialogErro = '';
        this.dialogCorrespondenciaVisivel = false;
        this.dialogCarregando.set(false);
    }

    fecharDialogCrmGerado(): void {
        this.dialogCrmGeradoVisivel = false;
    }

    private abrirDialogCorrespondencia(rotuloAcao: string): void {
        const idSolicitacao = this.solicitacaoSelecionada?.idSolicitacao;
        if (!idSolicitacao) return;

        this.dialogTitulo = `${rotuloAcao} - Solicitação ${idSolicitacao}`;
        this.dialogCorrespondenciaVisivel = true;
        this.dialogCarregando.set(true);
        this.dialogErro = '';
        this.correspondenciasHtml = [];
        this.revogarUrlPdf();
        this.certificadoPdfUrl = null;

        let pdfErro: string | null = null;

        forkJoin({
            html: this.centralTransferenciaService.getCorrespondenciaHtml(idSolicitacao).pipe(catchError(() => of(null))),
            pdf: this.centralTransferenciaService.getCertificadoRegularidadePdf(idSolicitacao, this.nomeUsuario, this.nomeUsuario).pipe(
                catchError((err) => {
                    pdfErro = this.extrairMensagemErro(err) ?? null;
                    return of(null);
                })
            )
        })
            .pipe(
                finalize(() => {
                    this.dialogCarregando.set(false);
                })
            )
            .subscribe({
                next: ({ html, pdf }) => {
                    const pdfDisponivel = !!pdf;
                    this.correspondenciasHtml = this.mapearCorrespondencias(html, !pdfDisponivel);
                    if (pdf) {
                        this._rawPdfUrl = URL.createObjectURL(pdf);
                        this.certificadoPdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this._rawPdfUrl);
                    }
                    if (!this.certificadoPdfUrl && !this.correspondenciasHtml.length) {
                        this.dialogErro = pdfErro ?? 'Nenhuma correspondência disponível para esta solicitação.';
                    }
                },
                error: (erro) => {
                    this.dialogErro = this.extrairMensagemErro(erro) ?? 'Não foi possível carregar as correspondências da solicitação.';
                }
            });
    }

    private mapearCorrespondencias(response?: CorrespondenciaHtmlDto | null, incluirCertificadoHtml = false): Array<{ titulo: string; html: string }> {
        const correspondencias: Array<{ titulo: string; html: string }> = [];

        if (incluirCertificadoHtml) {
            const certificadoRegularidade = this.valorTexto(response?.certificadoRegularidade);
            if (certificadoRegularidade) {
                correspondencias.push({ titulo: 'Certificado de Regularidade', html: certificadoRegularidade });
            }
        }

        const oficioConfirmacao = this.valorTexto(response?.oficioConfirmacao);
        if (oficioConfirmacao) {
            correspondencias.push({ titulo: 'Ofício de Confirmação', html: oficioConfirmacao });
        }

        const oficioRepudio = this.valorTexto(response?.oficioRepudio);
        if (oficioRepudio) {
            correspondencias.push({ titulo: 'Ofício de Repúdio', html: oficioRepudio });
        }

        return correspondencias;
    }

    private valorTexto(valor: unknown): string {
        return (valor ?? '').toString().trim();
    }

    private criarFiltrosVazios(): FiltroAbertasUfDestino {
        return {
            id: '',
            nomeMedico: '',
            crm: '',
            cpf: '',
            ufOrigem: '',
            tipo: ''
        };
    }

    private aplicarListaFiltrada(): void {
        const idFiltro = this.valorTexto(this.filtrosAplicados.id).toLowerCase();
        const nomeFiltro = this.valorTexto(this.filtrosAplicados.nomeMedico).toLowerCase();
        const crmFiltro = this.normalizarCrm(this.filtrosAplicados.crm);
        const cpfFiltro = this.normalizarCrm(this.filtrosAplicados.cpf);
        const ufOrigemFiltro = this.valorTexto(this.filtrosAplicados.ufOrigem).toUpperCase();
        const tipoFiltro = this.valorTexto(this.filtrosAplicados.tipo).toUpperCase();

        this.solicitacoesFiltradas = this.solicitacoes.filter((item) => {
            const id = this.valorTexto(item.idSolicitacao).toLowerCase();
            const nome = this.valorTexto(item.nomeMedico).toLowerCase();
            const crm = this.normalizarCrm(item.crm);
            const cpf = this.normalizarCrm(item.cpf);
            const ufOrigem = this.valorTexto(item.ufOrigem).toUpperCase();
            const tipo = this.valorTexto(item.tipoSolicitacao).toUpperCase();

            const correspondeId = !idFiltro || id.includes(idFiltro);
            const correspondeNome = !nomeFiltro || nome.includes(nomeFiltro);
            const correspondeCrm = !crmFiltro || crm.includes(crmFiltro);
            const correspondeCpf = !cpfFiltro || cpf.includes(cpfFiltro);
            const correspondeUfOrigem = !ufOrigemFiltro || ufOrigem.includes(ufOrigemFiltro);
            const correspondeTipo = !tipoFiltro || tipo === tipoFiltro;

            return correspondeId && correspondeNome && correspondeCrm && correspondeCpf && correspondeUfOrigem && correspondeTipo;
        });

        if (!this.solicitacaoSelecionada?.idSolicitacao) {
            return;
        }

        const selecionadaAindaExiste = this.solicitacoesFiltradas.some((item) => item.idSolicitacao === this.solicitacaoSelecionada?.idSolicitacao);
        if (!selecionadaAindaExiste) {
            this.solicitacaoSelecionada = null;
            this.customTable()?.selectedRow.set(null);
        }
    }

    private confirmarAcaoUsuario(titulo: string, mensagem: string, acaoConfirmada: () => void): void {
        this.confirmationService.confirm({
            header: titulo,
            message: mensagem,
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Sim',
            rejectLabel: 'Não',
            accept: acaoConfirmada
        });
    }

    private executarAcao(nomeAcao: string, mensagemSucessoPadrao: string, fnAcao: (idSolicitacao: number, crm: string, uf: string, crmGerado?: string) => Observable<CentralTransferenciaDto>): void {
        if (this.carregandoAcao()) {
            return;
        }

        const idSolicitacao = this.solicitacaoSelecionada?.idSolicitacao;
        const crm = this.normalizarCrm(this.solicitacaoSelecionada?.crm);
        const uf = this.normalizarUf(this.ufDestino);
        const crmGerado = this.normalizarCrm(this.solicitacaoSelecionada?.nuCrmGerado) || crm;

        if (!idSolicitacao || !crm || !uf) {
            this.definirErroAcao('Não foi possível executar a ação: dados obrigatórios da solicitação estão incompletos.');
            return;
        }

        if (nomeAcao === 'confirmar' && !crmGerado) {
            this.definirErroAcao('Não foi possível confirmar: CRM gerado não informado.');
            return;
        }

        this.feedbackAcao = '';
        this.erroAcao = false;
        this.carregandoAcao.set(true);
        fnAcao(idSolicitacao, crm, uf, crmGerado)
            .pipe(
                finalize(() => {
                    this.carregandoAcao.set(false);
                })
            )
            .subscribe({
                next: (response) => {
                    this.feedbackAcao = this.valorTexto(response?.descricaoStatus) || mensagemSucessoPadrao;
                    this.erroAcao = false;
                    this.carregarAbertasUfDestino();
                },
                error: (erro) => {
                    this.definirErroAcao(this.extrairMensagemErro(erro) ?? `Não foi possível ${nomeAcao} a solicitação.`);
                }
            });
    }

    private executarConfirmacaoComCadastro(): void {
        if (this.carregandoAcao()) {
            return;
        }

        const idSolicitacao = this.solicitacaoSelecionada?.idSolicitacao;
        const crm = this.normalizarCrm(this.solicitacaoSelecionada?.crm);
        const uf = this.normalizarUf(this.ufDestino);

        if (!idSolicitacao || !crm || !uf) {
            this.definirErroAcao('Não foi possível executar a ação: dados obrigatórios da solicitação estão incompletos.');
            return;
        }

        this.feedbackAcao = '';
        this.erroAcao = false;
        this.carregandoAcao.set(true);
        this.dialogLoadingConfirmacaoVisivel = true;
        this.crmGeradoConfirmacao = '';
        this.ufCrmGeradoConfirmacao = '';

        this.centralTransferenciaService
            .cadastrarMedicoPorIdSolicitacao(idSolicitacao)
            .pipe(
                switchMap((cadastro) => {
                    const crmGerado = this.normalizarCrm(cadastro.crmGerado);
                    const ufCrmGerado = this.normalizarUf(cadastro.ufDestino) || uf;

                    if (!crmGerado || !ufCrmGerado) {
                        this.definirErroAcao('Não foi possível confirmar: CRM ou UF gerados não foram retornados pelo cadastro.');
                        return EMPTY;
                    }

                    return this.centralTransferenciaService.confirmar(idSolicitacao, crm, uf, crmGerado).pipe(
                        tap(() => {
                            this.feedbackAcao = `Confirmação realizada com sucesso. CRM gerado: ${crmGerado}/${ufCrmGerado}.`;
                            this.erroAcao = false;
                            this.crmGeradoConfirmacao = crmGerado;
                            this.ufCrmGeradoConfirmacao = ufCrmGerado;
                            this.dialogCrmGeradoVisivel = true;
                        })
                    );
                }),
                finalize(() => {
                    this.carregandoAcao.set(false);
                    this.dialogLoadingConfirmacaoVisivel = false;
                })
            )
            .subscribe({
                next: () => {
                    this.carregarAbertasUfDestino();
                },
                error: (erro) => {
                    this.definirErroAcao(this.extrairMensagemErro(erro) ?? 'Não foi possível confirmar a solicitação.');
                }
            });
    }

    private definirErroAcao(mensagem: string): void {
        this.feedbackAcao = mensagem;
        this.erroAcao = true;
    }

    private normalizarCrm(valor: unknown): string {
        return (valor ?? '').toString().trim().replace(/\D+/g, '');
    }

    private normalizarUf(valor: unknown): string {
        return (valor ?? '')
            .toString()
            .trim()
            .toUpperCase()
            .replace(/[^A-Z]/g, '')
            .slice(0, 2);
    }

    private revogarUrlPdf(): void {
        if (this._rawPdfUrl) {
            URL.revokeObjectURL(this._rawPdfUrl);
            this._rawPdfUrl = null;
        }
    }

    private extrairMensagemErro(erro: unknown): string | undefined {
        const e = erro as { error?: { message?: string; detail?: string; error?: string }; message?: string; statusText?: string };
        return e?.error?.message || e?.error?.detail || e?.error?.error || e?.message || e?.statusText;
    }

    private async atualizarPermissaoAcoes(ufBase: string): Promise<void> {
        const ufValidacao = this.normalizarUf(ufBase);
        if (!ufValidacao) {
            this.podeExibirAcoes.set(false);
            return;
        }

        try {
            const resultado = await firstValueFrom(this.permissaoService.validar({ validacoes: { mesmaUf: ufValidacao } }));
            this.podeExibirAcoes.set(!!(resultado?.validacoes?.['mesmaUf'] ?? false));
        } catch {
            this.podeExibirAcoes.set(false);
        }
    }
}
